// crewbit-admin/src/hooks/useRouteRibbonSummary.ts
//
// Schedule Module Phase C C4 — admin pair of mobile useRouteRibbonSummary.
// Body parallel modulo runtime per feedback_admin_pair_must_match_mobile_body.md.
//
// Admin's useScheduleEntries signature differs (takes businessId + Date + viewMode);
// caller bridges by passing the businessId + Date to this hook directly.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §13 + §5.4.

import { useMemo } from 'react';
import { useScheduleEntries } from './useScheduleEntries';
import { estimateDriveMinutes, haversineKm } from '../utils/driveTime';
import type { ScheduleEntry } from '../types/schedule';

export interface RouteRibbonSummary {
  stopsCount: number;
  distanceKm: number;
  driveTimeMinutes: number;
  lastOptimizedAt?: string | null;
}

function locationBearing(entry: ScheduleEntry): boolean {
  return entry.location_lat != null && entry.location_lng != null;
}

export interface UseRouteRibbonSummaryResult {
  summary: RouteRibbonSummary;
  loading: boolean;
  stops: ScheduleEntry[];
}

export function useRouteRibbonSummary(
  businessId: string | undefined,
  anchorDate: Date,
): UseRouteRibbonSummaryResult {
  const { entries, loading } = useScheduleEntries(businessId, anchorDate, 'day');

  return useMemo(() => {
    const stops = entries
      .filter(locationBearing)
      .sort((a, b) => a.start_at.localeCompare(b.start_at));

    let totalKm = 0;
    let totalMinutes = 0;
    for (let i = 0; i < stops.length - 1; i++) {
      const a = stops[i];
      const b = stops[i + 1];
      const aLat = a.location_lat as number;
      const aLng = a.location_lng as number;
      const bLat = b.location_lat as number;
      const bLng = b.location_lng as number;
      totalKm += haversineKm(aLat, aLng, bLat, bLng);
      totalMinutes += estimateDriveMinutes(aLat, aLng, bLat, bLng);
    }

    return {
      summary: {
        stopsCount: stops.length,
        distanceKm: Math.round(totalKm * 10) / 10,
        driveTimeMinutes: totalMinutes,
        lastOptimizedAt: null,
      },
      loading,
      stops,
    };
  }, [entries, loading]);
}
